import {z} from 'zod';

export const PriceSchema = z.object({
  amount: z.number().optional(),
  currency: z.string().optional(),
});

export const ProfitSchema = PriceSchema.extend({margin: z.number().optional()});

export const ProfitabilityOrderTypeSchema = z.object({
  salesBasePrice: PriceSchema.optional(),
  discount: PriceSchema.optional(),
  sellingPrice: PriceSchema.optional(),
  profit: ProfitSchema.optional(),
});

export const SummarySchema = z.object({
  totalTurnover: PriceSchema.optional(),
  averageTurnoverPerServiceCase: PriceSchema.optional(),
  averageTurnoverPerServiceOrder: PriceSchema.optional(),
  totalProfit: PriceSchema.extend({margin: z.number().optional()}).optional(),
});

export const ServiceOrdersProfitabilitySchema = z.object({
  commercial: ProfitabilityOrderTypeSchema.optional(),
  warranty: ProfitabilityOrderTypeSchema.optional(),
  insurance: ProfitabilityOrderTypeSchema.optional(),
  contract: ProfitabilityOrderTypeSchema.optional(),
  claims: ProfitabilityOrderTypeSchema.optional(),
  internal: ProfitabilityOrderTypeSchema.optional(),
});

const RevenueSchema = z.object({
  withoutVat: z.number(),
  vat: z.number(),
  withVat: z.number(),
});

const ProfitabilitySummarySchema = z.object({
  serviceCaseCount: z.number(),
  serviceOrderCount: z.number(),
  soldHours: z.number(),
  totalRevenue: RevenueSchema,
  labourRevenue: RevenueSchema,
  materialRevenue: RevenueSchema,
  smallUnitMaterialRevenue: RevenueSchema,
  currency: z.string(),
});

const ProfitabilityHistoryItemSchema = z.object({
  year: z.number(),
  serviceCaseCount: z.number(),
  serviceOrderCount: z.number(),
  soldHours: z.number(),
  totalRevenue: RevenueSchema,
  labourRevenue: RevenueSchema,
  materialRevenue: RevenueSchema,
  smallUnitMaterialRevenue: RevenueSchema,
  currency: z.string(),
});

export const GetHistoryOrderProfitabilityApiResponseSchema = z.object({
  summary: ProfitabilitySummarySchema,
  history: z.array(ProfitabilityHistoryItemSchema),
});

export type GetHistoryOrderProfitabilityApiResponse = z.infer<
  typeof GetHistoryOrderProfitabilityApiResponseSchema
>;

export const GetHistoryOrderProfitabilityApiArgSchema = z.object({vehicleId: z.string()});

export type GetHistoryOrderProfitabilityApiArg = z.infer<
  typeof GetHistoryOrderProfitabilityApiArgSchema
>;

export const ServiceCaseByVehicleCountResponseBodySchema = z.object({
  count: z.number(),
});

export type ServiceCaseByVehicleCountResponseBody = z.infer<
  typeof ServiceCaseByVehicleCountResponseBodySchema
>;

export const ServiceCaseByVehicleCountStatusSchema = z.enum(['ACTIVE', 'FINISHED']);

export type ServiceCaseByVehicleCountStatus = z.infer<typeof ServiceCaseByVehicleCountStatusSchema>;

export const GetServiceCaseByVehicleCountApiArgSchema = z.object({
  vehicleId: z.string(),
  status: ServiceCaseByVehicleCountStatusSchema.optional(),
});

export type GetServiceCaseByVehicleCountApiArg = z.infer<
  typeof GetServiceCaseByVehicleCountApiArgSchema
>;

export const GetServiceCaseByVehicleCountApiResponseSchema =
  ServiceCaseByVehicleCountResponseBodySchema;

export type GetServiceCaseByVehicleCountApiResponse = z.infer<
  typeof GetServiceCaseByVehicleCountApiResponseSchema
>;

export const AnnualMileageRequestSchema = z.object({
  vehicleId: z.string(),
});

export const AnnualMileageResponseSchema = z.object({
  annualMileage: z.number().optional(),
});

export type AnnualMileageRequest = z.infer<typeof AnnualMileageRequestSchema>;
export type AnnualMileageResponse = z.infer<typeof AnnualMileageResponseSchema>;
